<template>
  <div>
    <v-card class="mb-3">
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="6" md="3" v-if="role === 'SUPERADMIN'">
            <v-select
              outlined dense hide-details
              :items="offices"
              item-text="name"
              item-value="id"
              label="Sede"
              clearable
              v-model="filters.office"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field outlined dense hide-details type="date" label="Fecha desde" v-model="filters.min_date"></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field outlined dense hide-details type="date" label="Fecha hasta" v-model="filters.max_date"></v-text-field>
          </v-col>
          <v-col cols="auto">
            <v-btn color="secondary" @click="loadSales">Buscar</v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto">
            <v-btn color="success" @click="$router.push('/retail/create')">
              <v-icon left>mdi-plus</v-icon> Nueva venta
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card>
      <v-data-table
        :headers="headers"
        :items="sales"
        hide-default-footer
        :loading="$store.state.retail.isLoading"
        no-data-text="No hay ventas"
        :items-per-page="20"
      >
        <template v-slot:[`item.total`]="{ item }">{{ item.total | currency }}</template>
        <template v-slot:[`item.tip`]="{ item }">{{ item.tip ? (item.tip | currency) : '—' }}</template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="item.status === 'COMPLETED' ? 'success' : 'error'" text-color="white" small>
            {{ item.status === 'COMPLETED' ? 'Completada' : 'Cancelada' }}
          </v-chip>
        </template>
        <template v-slot:[`item.office`]="{ item }">{{ item.office ? item.office.name : '—' }}</template>
        <template v-slot:[`item.user`]="{ item }">{{ item.user ? item.user.name : '—' }}</template>
        <template v-slot:[`item.employee`]="{ item }">{{ item.employee ? item.employee.full_name : '—' }}</template>
        <template v-slot:[`item.payment_method`]="{ item }">
          <span v-if="item.payment_method">{{ paymentMethodLabel(item.payment_method) }}</span>
          <span v-else>—</span>
        </template>
        <template v-slot:[`item.created_at`]="{ item }">{{ item.created_at | shortDate }}</template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="primary" v-bind="attrs" v-on="on" class="px-1" @click="showSale(item)">
                mdi-eye-outline
              </v-icon>
            </template>
            <span>VER</span>
          </v-tooltip>
          <v-tooltip bottom v-if="item.status === 'COMPLETED'">
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="error" v-bind="attrs" v-on="on" class="px-1" @click="confirmCancel(item)">
                mdi-cancel
              </v-icon>
            </template>
            <span>CANCELAR</span>
          </v-tooltip>
        </template>
      </v-data-table>
      <v-pagination v-model="page" :length="totalPages" :total-visible="8" circle class="mt-2" @input="loadSales"></v-pagination>
    </v-card>

    <!-- View sale dialog -->
    <v-dialog v-model="dialogView" width="600" persistent>
      <v-card v-if="selectedSale">
        <v-card-title>Venta {{ selectedSale.reference }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-3">
          <div v-if="selectedSale.client_name"><b>Cliente:</b> {{ selectedSale.client_name }} — {{ selectedSale.client_id_number }}</div>
          <div><b>Asesor:</b> {{ selectedSale.employee ? selectedSale.employee.full_name : '—' }}</div>
          <div><b>Método de pago:</b> {{ selectedSale.payment_method ? paymentMethodLabel(selectedSale.payment_method) : '—' }}</div>
          <div><b>Sede:</b> {{ selectedSale.office ? selectedSale.office.name : '—' }}</div>
          <div><b>Cajero:</b> {{ selectedSale.user ? selectedSale.user.name : '—' }}</div>
          <v-data-table
            class="mt-3"
            :headers="itemHeaders"
            :items="selectedSale.items"
            hide-default-footer
            dense
            :items-per-page="50"
          >
            <template v-slot:[`item.unit_price`]="{ item }">{{ item.unit_price | currency }}</template>
            <template v-slot:[`item.subtotal`]="{ item }">{{ item.subtotal | currency }}</template>
          </v-data-table>
          <div class="text-right mt-2 font-weight-bold text-h6">Total: {{ selectedSale.total | currency }}</div>
          <div v-if="selectedSale.tip" class="text-right font-weight-bold">Propina: {{ selectedSale.tip | currency }}</div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="error" @click="dialogView = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cancel confirm -->
    <v-dialog v-model="dialogCancel" width="450" persistent>
      <v-card>
        <v-card-title>Cancelar venta</v-card-title>
        <v-card-text v-if="selectedSale">
          ¿Cancelar la venta <b>{{ selectedSale.reference }}</b>? El stock de los productos será devuelto.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogCancel = false">No</v-btn>
          <v-btn text color="error" @click="doCancel">Sí, cancelar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { SET_OFFICES } from "@/store/offices";
export default {
  name: "RetailSales",
  filters: {
    currency(v) {
      return new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP" }).format(v);
    },
    shortDate(v) { return v ? v.substring(0, 16).replace("T", " ") : ""; },
  },
  data: () => ({
    sales: [],
    offices: [],
    page: 1,
    totalPages: 1,
    filters: { office: null, min_date: "", max_date: "" },
    dialogView: false,
    dialogCancel: false,
    selectedSale: null,
    headers: [
      { text: "Referencia", value: "reference" },
      { text: "Cliente", value: "client_name", sortable: false },
      { text: "Total", value: "total" },
      { text: "Propina", value: "tip" },
      { text: "Estado", value: "status" },
      { text: "Asesor", value: "employee", sortable: false },
      { text: "Método de pago", value: "payment_method", sortable: false },
      { text: "Sede", value: "office", sortable: false },
      { text: "Cajero", value: "user", sortable: false },
      { text: "Fecha", value: "created_at" },
      { text: "Acciones", value: "actions", sortable: false },
    ],
    itemHeaders: [
      { text: "Producto", value: "product_name" },
      { text: "Cant.", value: "quantity" },
      { text: "Precio", value: "unit_price" },
      { text: "Subtotal", value: "subtotal" },
    ],
  }),
  computed: {
    role() { return this.$store.state.user.user?.role; },
  },
  created() {
    this.loadSales();
    this.$store.dispatch("getOfficesAll");
    this.$store.subscribe((m, s) => {
      if (m.type === SET_OFFICES) this.offices = s.offices.offices;
    });
  },
  methods: {
    loadSales() {
      let q = "page=" + this.page + "&";
      if (this.filters.office) q += "office=" + this.filters.office + "&";
      if (this.filters.min_date) q += "min_date=" + this.filters.min_date + "&";
      if (this.filters.max_date) q += "max_date=" + this.filters.max_date + "&";
      this.$store.dispatch("getRetailSales", q).then(() => {
        const data = this.$store.state.retail.sales;
        this.sales = data.data || data;
        this.totalPages = data.last_page || 1;
      });
    },
    showSale(sale) {
      this.selectedSale = sale;
      this.dialogView = true;
    },
    confirmCancel(sale) {
      this.selectedSale = sale;
      this.dialogCancel = true;
    },
    paymentMethodLabel(m) {
      const labels = { CASH: "Efectivo", TRANSFER: "Transferencia", DATAPHONE: "Datáfono", GIFT_CARD: "Gift Card" };
      return labels[m] || m;
    },
    doCancel() {
      this.$store.dispatch("cancelRetailSale", this.selectedSale.id).then(() => {
        this.dialogCancel = false;
        this.loadSales();
      });
    },
  },
};
</script>
